import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, Menu, MenuItem, ListItemIcon } from "@mui/material";
import { useState, type ReactNode } from "react";

export type SortDir = "asc" | "desc";

export type TableColumn<T> = {
  id: string;
  header: string;
  renderCell: (row: T) => ReactNode;
  getTitle?: (row: T) => string;
  primary?: boolean;
  primaryLeading?: () => ReactNode;
  sortable?: boolean;
  headerLeading?: () => ReactNode;
  align?: "left" | "center" | "right";
  minWidthPx?: number;
};

export type RowMenuItem<T> = {
  id: string;
  label: string;
  icon?: ReactNode;
  tone?: "default" | "danger";
  onSelect: (row: T) => void;
};

type DataTableProps<T> = {
  columns: TableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  sortColumnId: string | null;
  sortDir: SortDir;
  onSortChange: (columnId: string, dir: SortDir) => void;
  selectedRowId: string | null;
  onSelectRow: (id: string | null) => void;
  rowMenuItems?: RowMenuItem<T>[];
  loading?: boolean;
  emptyPlaceholder?: ReactNode;
};

export function DataTable<T>({
  columns,
  rows,
  getRowId,
  sortColumnId,
  sortDir,
  onSortChange,
  selectedRowId: _selectedRowId,
  onSelectRow: _onSelectRow,
  rowMenuItems,
  emptyPlaceholder,
}: DataTableProps<T>) {
  const [menuAnchor, setMenuAnchor] = useState<null | { el: HTMLElement; row: T }>(null);

  if (rows.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        {emptyPlaceholder ?? <span className="ds-text-secondary">No data.</span>}
      </div>
    );
  }

  return (
    <>
      <table className="ds-dt">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.id}
                style={{
                  textAlign: col.align ?? "left",
                  minWidth: col.minWidthPx,
                  width: col.minWidthPx,
                }}
                className={col.sortable ? "ds-dt__sortable" : undefined}
                onClick={() => {
                  if (!col.sortable) return;
                  if (sortColumnId === col.id) {
                    onSortChange(col.id, sortDir === "asc" ? "desc" : "asc");
                  } else {
                    onSortChange(col.id, "asc");
                  }
                }}
              >
                <span className="ds-dt__th-inner">
                  {col.headerLeading?.()}
                  {col.header}
                  {col.sortable && sortColumnId === col.id ? (sortDir === "asc" ? " ▲" : " ▼") : null}
                </span>
              </th>
            ))}
            {rowMenuItems && rowMenuItems.length > 0 ? <th className="ds-dt__row-menu" aria-label="actions" /> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const id = getRowId(row);
            return (
              <tr key={id}>
                {columns.map((col) => (
                  <td key={col.id} style={{ textAlign: col.align ?? "left" }} title={col.getTitle?.(row)}>
                    {col.primary ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {col.primaryLeading?.()}
                        {col.renderCell(row)}
                      </div>
                    ) : (
                      col.renderCell(row)
                    )}
                  </td>
                ))}
                {rowMenuItems && rowMenuItems.length > 0 ? (
                  <td className="ds-dt__row-menu">
                    <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row })} aria-label="Open row menu">
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
      <Menu anchorEl={menuAnchor?.el} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {rowMenuItems?.map((item) => (
          <MenuItem
            key={item.id}
            onClick={() => {
              if (menuAnchor) item.onSelect(menuAnchor.row);
              setMenuAnchor(null);
            }}
            sx={item.tone === "danger" ? { color: "error.main" } : undefined}
          >
            {item.icon ? <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon> : null}
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
